import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Check, X, RefreshCw, ShieldCheck, Copy, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin: manage club access code + review pending sign-ups.
 *
 * Feb 2026 lockdown (production incident — anyone could sign up and join a
 * comp). Signups without the code go into an approval queue and can't log in
 * until an admin approves them here.
 */
export default function AdminAccessControl({ open, onClose }) {
  const [code, setCode] = useState("");
  const [savedCode, setSavedCode] = useState("");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [codeRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/settings/club-access-code`, { withCredentials: true }),
        axios.get(`${API}/admin/pending-users`, { withCredentials: true }),
      ]);
      setSavedCode(codeRes.data?.code || "");
      setCode(codeRes.data?.code || "");
      setPending(pendingRes.data?.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load access control");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleSaveCode = async () => {
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      toast.error("Access code must be at least 4 characters");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/club-access-code`, { code: trimmed }, { withCredentials: true });
      setSavedCode(trimmed);
      toast.success("Access code updated", {
        description: "Share it with new members — old signups already in the queue still need approval below.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save code");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(savedCode);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const handleBroadcastCode = async () => {
    if (!savedCode) {
      toast.error("Save a code first, then broadcast it.");
      return;
    }
    if (!window.confirm(
      `Email the current access code (${savedCode}) to all approved members?\n\n` +
      "Existing members don't need to do anything with it — this is just so they can pass it on to new members."
    )) return;
    setBroadcasting(true);
    try {
      const res = await axios.post(
        `${API}/admin/settings/club-access-code/broadcast`,
        {},
        { withCredentials: true }
      );
      toast.success(`Emailed to ${res.data?.sent ?? 0} member(s)`, {
        description: res.data?.failed
          ? `${res.data.failed} email(s) failed — check server logs.`
          : "All approved members now have the code.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to broadcast code");
    } finally {
      setBroadcasting(false);
    }
  };

  const handleApprove = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/pending-users/approve`, { user_id: userId }, { withCredentials: true });
      toast.success(`Approved ${email}`);
      setPending((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const handleReject = async (userId, email) => {
    if (!window.confirm(`Reject ${email}? They will not be able to sign in.`)) return;
    try {
      await axios.post(`${API}/admin/pending-users/reject`, { user_id: userId }, { withCredentials: true });
      toast.success(`Rejected ${email}`);
      setPending((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="access-control-modal" className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-ink">
            <ShieldCheck className="w-5 h-5 text-accent-deep" />
            Signup access control
          </DialogTitle>
          <DialogDescription>
            New sign-ups need a shared club access code. Without it, the account goes into the
            approval queue below.
          </DialogDescription>
        </DialogHeader>

        {/* Access code editor */}
        <div className="rounded-lg border border-line bg-app p-4 space-y-3">
          <Label className="text-sm text-ink">Club access code</Label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <KeyRound className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <Input
                data-testid="club-access-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. ROCH-2026"
                className="pl-8 uppercase tracking-widest"
                autoComplete="off"
              />
            </div>
            <Button
              data-testid="save-club-access-code-btn"
              onClick={handleSaveCode}
              disabled={saving || code.trim() === savedCode}
              className="bg-accent-deep hover:bg-accent-deep"
            >
              {saving ? "Saving…" : "Save code"}
            </Button>
            {savedCode && (
              <Button
                variant="outline"
                onClick={handleCopyCode}
                data-testid="copy-club-access-code-btn"
                title="Copy current code"
              >
                <Copy className="w-4 h-4" />
              </Button>
            )}
          </div>
          <p className="text-xs text-ink-muted">
            Share this with your members via email/WhatsApp. Anyone who enters it during signup is
            auto-approved. Rotate it whenever you want to close the door to old links.
          </p>
          <div className="rounded-md bg-accent-tint border border-accent/20 px-3 py-2 text-[12px] text-ink">
            <span className="font-semibold">Existing members are not affected.</span> They keep
            signing in with their password / Google as before — the code is only for NEW sign-ups.
          </div>
          {savedCode && (
            <Button
              onClick={handleBroadcastCode}
              disabled={broadcasting}
              variant="outline"
              size="sm"
              data-testid="broadcast-club-access-code-btn"
              className="flex items-center gap-2 border-accent/40 text-ink hover:bg-accent-tint-2"
            >
              <Send className="w-4 h-4" />
              {broadcasting ? "Emailing…" : "Email code to all members"}
            </Button>
          )}
        </div>

        {/* Pending approvals */}
        <div className="mt-4 rounded-lg border border-line overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-app">
            <div>
              <div className="font-semibold text-ink text-sm">Pending approvals</div>
              <div className="text-xs text-ink-muted">
                {pending.length} account{pending.length === 1 ? "" : "s"} waiting
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
              data-testid="refresh-pending-users-btn"
              className="flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          <div className="divide-y divide-line">
            {loading && (
              <div className="px-3 py-6 text-center text-ink-muted text-sm">Loading…</div>
            )}
            {!loading && pending.length === 0 && (
              <div className="px-3 py-6 text-center text-ink-muted text-sm" data-testid="pending-empty-state">
                No pending users right now.
              </div>
            )}
            {!loading && pending.map((u) => {
              // Waiting-time badge — flags accounts that have been sitting
              // in the queue too long. Helps admins prioritise.
              let waitingLabel = "";
              let urgency = "normal";
              if (u.created_at) {
                const createdMs = new Date(u.created_at).getTime();
                const hours = Math.max(0, (Date.now() - createdMs) / 3_600_000);
                if (hours < 1) {
                  waitingLabel = "just now";
                } else if (hours < 24) {
                  waitingLabel = `${Math.floor(hours)}h waiting`;
                  if (hours >= 6) urgency = "medium";
                } else {
                  const days = Math.floor(hours / 24);
                  waitingLabel = `${days}d waiting`;
                  urgency = "high";
                }
              }
              const urgencyClass = urgency === "high"
                ? "bg-red-50 text-red-700 border-red-200"
                : urgency === "medium"
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-ink-tint text-ink-muted border-line";
              return (
              <div key={u.id} className="px-3 py-3 flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <div className="font-medium text-ink truncate">{u.name || "(no name)"}</div>
                    <span
                      data-testid={`pending-badge-${u.id}`}
                      className="text-[10px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
                    >
                      Pending
                    </span>
                    {waitingLabel && (
                      <span
                        data-testid={`pending-waiting-${u.id}`}
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${urgencyClass}`}
                        title={u.created_at ? new Date(u.created_at).toLocaleString("en-GB") : ""}
                      >
                        {waitingLabel}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted truncate">{u.email}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    Signed up via {u.auth_type === "google" ? "Google" : "email/password"}
                    {u.created_at ? ` · ${new Date(u.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleApprove(u.id, u.email)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                  data-testid={`approve-user-${u.id}-btn`}
                >
                  <Check className="w-4 h-4" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(u.id, u.email)}
                  className="text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-1"
                  data-testid={`reject-user-${u.id}-btn`}
                >
                  <X className="w-4 h-4" />
                  Reject
                </Button>
              </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
